import React, { useState, useEffect, useMemo } from 'react';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid2,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE } from '../../hooks/useModal';
import AutocompleteSelect from '../atom/AutoCompleteSelect';
import { venuesAutocomplete } from '../../autocomplete';
import RichTextEditor from '../richtexteditor/RichTextEditor';
import { useGetCountryCodes, getLocationCodes } from '@openapi/common/common';
import { useGetArrangements } from '@openapi/shows/shows';
import SlotTemplateEditor from './SlotTemplateEditor';

/**
 * Settings section - handles venue, timing, policies, and configuration.
 * The fields are split across sub-tabs (General / Booking rules / Content &
 * policies / Notifications / Pricing) so this dense section stays scannable;
 * each sub-tab shows a red badge when it holds a touched validation error.
 */
const ShowSettingsSection = React.memo(
    ({ values, setFieldValue, touched, errors, canManageVenues, showModal, showId }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const stackTabs = useMediaQuery(theme.breakpoints.down('md'));
        const [subTab, setSubTab] = useState(0);
        const [locationOptions, setLocationOptions] = useState([]);

        const { data: arrangementsData } = useGetArrangements(showId, {
            query: { enabled: !!showId },
        });
        const arrangements = useMemo(
            () => arrangementsData?.arrangements || [],
            [arrangementsData],
        );

        // Fetch country codes
        const { data: countriesData } = useGetCountryCodes();
        const countries = useMemo(() => countriesData?.countries || [], [countriesData]);

        // Fetch location codes based on selected country
        useEffect(() => {
            const fetchLocations = async () => {
                try {
                    const params = values.countryCode ? { countryCode: values.countryCode } : {};
                    const response = await getLocationCodes(params);
                    setLocationOptions(response.locations || []);
                } catch (error) {
                    console.error('Failed to fetch location codes:', error);
                    setLocationOptions([]);
                }
            };
            fetchLocations();
        }, [values.countryCode]);

        // Find selected country and location objects for autocomplete
        const selectedCountry = useMemo(
            () => countries.find((c) => c.code === values.countryCode) || null,
            [countries, values.countryCode],
        );

        const selectedLocation = useMemo(
            () => locationOptions.find((l) => l.code === values.destinationCode) || null,
            [locationOptions, values.destinationCode],
        );

        const updateReason = (idx, patch) => {
            const updated = [...(values.cancellationReasons || [])];
            updated[idx] = { ...updated[idx], ...patch };
            setFieldValue('cancellationReasons', updated);
        };

        // Sub-tab 0: General - venue, location, timing, timeslot management
        const generalFields = (
            <>
                {/* Venue */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <AutocompleteSelect
                        initCall={venuesAutocomplete.init}
                        autocompleteCall={venuesAutocomplete.search}
                        value={values?.venueId}
                        setValue={(ev) => setFieldValue('venueId', ev)}
                        disabled={!canManageVenues}
                    >
                        <TextField label={t('venues.model')} required />
                    </AutocompleteSelect>
                </Grid2>

                {/* Show Location (URL) */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.location')}
                        value={values.location}
                        onChange={(ev) => setFieldValue('location', ev.target.value)}
                        error={touched.location && Boolean(errors.location)}
                        helperText={touched.location ? errors.location : ''}
                    />
                </Grid2>

                {/* Country Code */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        fullWidth
                        options={countries}
                        value={selectedCountry}
                        onChange={(_, newValue) => {
                            setFieldValue('countryCode', newValue?.code || null);
                            // Clear destination code if country changes
                            if (newValue?.code !== values.countryCode) {
                                setFieldValue('destinationCode', null);
                            }
                        }}
                        getOptionLabel={(option) => `${option.name} (${option.code})`}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('shows.columns.countryCode')}
                                error={touched.countryCode && Boolean(errors.countryCode)}
                                helperText={touched.countryCode ? errors.countryCode : ''}
                            />
                        )}
                    />
                </Grid2>

                {/* Destination Code */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        fullWidth
                        options={locationOptions}
                        value={selectedLocation}
                        onChange={(_, newValue) => {
                            setFieldValue('destinationCode', newValue?.code || null);
                        }}
                        getOptionLabel={(option) => `${option.name} (${option.code})`}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        disabled={!values.countryCode}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('shows.columns.destinationCode')}
                                error={touched.destinationCode && Boolean(errors.destinationCode)}
                                helperText={
                                    touched.destinationCode
                                        ? errors.destinationCode
                                        : !values.countryCode
                                          ? t('shows.columns.destinationCodeHelper')
                                          : ''
                                }
                            />
                        )}
                    />
                </Grid2>

                {/* Timing Settings */}
                <Grid2 size={{ xs: 6, sm: 3 }}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('shows.columns.cancelWindow')}
                        value={values.cancelWindow}
                        onChange={(ev) =>
                            setFieldValue('cancelWindow', parseInt(ev.target.value, 10))
                        }
                        error={touched.cancelWindow && Boolean(errors.cancelWindow)}
                        helperText={touched.cancelWindow ? errors.cancelWindow : ''}
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 3 }}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('shows.columns.reservationTime')}
                        value={values.reservationTime}
                        onChange={(ev) =>
                            setFieldValue('reservationTime', parseInt(ev.target.value, 10))
                        }
                        error={touched.reservationTime && Boolean(errors.reservationTime)}
                        helperText={touched.reservationTime ? errors.reservationTime : ''}
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 3 }}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('shows.columns.rollbackTimeSize')}
                        value={values.rollbackTimeSize}
                        onChange={(ev) =>
                            setFieldValue('rollbackTimeSize', parseInt(ev.target.value, 10))
                        }
                        error={touched.rollbackTimeSize && Boolean(errors.rollbackTimeSize)}
                        helperText={
                            (touched.rollbackTimeSize && errors.rollbackTimeSize) ||
                            (Number.isFinite(values.rollbackTimeSize) &&
                            values.rollbackTimeSize >= 60
                                ? t('shows.columns.rollbackTimeSizeHelp', {
                                      hours: Math.floor(values.rollbackTimeSize / 60),
                                      minutes: values.rollbackTimeSize % 60,
                                  })
                                : '')
                        }
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 3 }}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('shows.columns.bookingWindow')}
                        value={values.bookingWindow ?? ''}
                        onChange={(ev) => {
                            const val = ev.target.value;
                            setFieldValue('bookingWindow', val === '' ? null : parseInt(val, 10));
                        }}
                        error={touched.bookingWindow && Boolean(errors.bookingWindow)}
                        helperText={
                            (touched.bookingWindow && errors.bookingWindow) ||
                            (values.bookingWindow > 0
                                ? t('shows.columns.bookingWindowHelp', {
                                      hours: values.bookingWindow,
                                  })
                                : '')
                        }
                        inputProps={{ min: 0 }}
                    />
                </Grid2>

                {/* Timeslot Management */}
                <Grid2 size={{ xs: 12 }}>
                    <FormControl fullWidth>
                        <InputLabel id="timeslot-management-label">
                            {t('shows.columns.timeslotManagement')}
                        </InputLabel>
                        <Select
                            sx={{ textAlign: 'left' }}
                            labelId="timeslot-management-label"
                            value={values.isIcalEnabled}
                            onChange={(ev) => setFieldValue('isIcalEnabled', ev.target.value)}
                        >
                            <MenuItem value={0}>
                                {t('shows.timeslotManagementValues.manual')}
                            </MenuItem>
                            <MenuItem value={1}>
                                {t('shows.timeslotManagementValues.iCalSync')}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>

                {/* iCal Settings */}
                {values.isIcalEnabled === 1 && (
                    <>
                        <Grid2 size={{ xs: 8 }}>
                            <TextField
                                fullWidth
                                label={t('shows.columns.iCalUrl')}
                                value={values.icalUrl}
                                onChange={(ev) => setFieldValue('icalUrl', ev.target.value)}
                                error={touched.icalUrl && Boolean(errors.icalUrl)}
                                helperText={touched.icalUrl ? errors.icalUrl : ''}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 4 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('shows.columns.autoPublishDaysInAdvance')}
                                value={values.autoPublishDaysInAdvance}
                                onChange={(ev) =>
                                    setFieldValue(
                                        'autoPublishDaysInAdvance',
                                        parseInt(ev.target.value, 10),
                                    )
                                }
                                error={
                                    touched.autoPublishDaysInAdvance &&
                                    Boolean(errors.autoPublishDaysInAdvance)
                                }
                                helperText={
                                    touched.autoPublishDaysInAdvance
                                        ? errors.autoPublishDaysInAdvance
                                        : ''
                                }
                            />
                        </Grid2>
                    </>
                )}

                {arrangements.length > 0 && (
                    <Grid2 size={{ xs: 12 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('seats.activeArrangement')}</InputLabel>
                            <Select
                                value={values.activeArrangementId ?? ''}
                                label={t('seats.activeArrangement')}
                                onChange={(e) =>
                                    setFieldValue('activeArrangementId', e.target.value || null)
                                }
                                sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}
                            >
                                <MenuItem value="">{t('seats.defaultArrangement')}</MenuItem>
                                {arrangements.map((arr) => (
                                    <MenuItem key={arr.id} value={arr.id}>
                                        {arr.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid2>
                )}
            </>
        );

        // Sub-tab 1: Booking rules - guest-facing booking behavior toggles
        const bookingRulesFields = (
            <>
                <Grid2 size={{ xs: 12 }}>
                    <FormGroup
                        sx={{
                            textAlign: 'left',
                            '& .MuiFormControlLabel-root': { alignItems: 'flex-start' },
                            '& .MuiCheckbox-root': { pt: 0 },
                        }}
                    >
                        <FormControlLabel
                            label={t('shows.columns.allowCrossTicketBuying')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.allowCrossTicketBuying}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            allowCrossTicketBuying: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.absorbProcessingFee')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.absorbProcessingFee}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            absorbProcessingFee: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.delayedSeatAssignmentEnabled')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.delayedSeatAssignmentEnabled}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            delayedSeatAssignmentEnabled: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.delayedSeatAssignmentUseSeatIdsInEmails')}
                            control={
                                <Checkbox
                                    checked={
                                        !!values.marketing?.delayedSeatAssignmentUseSeatIdsInEmails
                                    }
                                    disabled={!values.marketing?.delayedSeatAssignmentEnabled}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            delayedSeatAssignmentUseSeatIdsInEmails:
                                                ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.allowSeatingGroupRequest')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.allowSeatingGroupRequest}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            allowSeatingGroupRequest: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.rentalMode')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.rentalMode}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            rentalMode: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.requireAdultTicket')}
                            control={
                                <Checkbox
                                    checked={!!values.marketing?.requireAdultTicket}
                                    onChange={(ev) =>
                                        setFieldValue('marketing', {
                                            ...values.marketing,
                                            requireAdultTicket: ev.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('shows.columns.guestCancellationEnabled')}
                            control={
                                <Checkbox
                                    checked={values.guestCancellationEnabled}
                                    onChange={(ev) =>
                                        setFieldValue('guestCancellationEnabled', ev.target.checked)
                                    }
                                />
                            }
                        />
                    </FormGroup>
                </Grid2>
                <SlotTemplateEditor
                    values={values}
                    setFieldValue={setFieldValue}
                    showModal={showModal}
                />
            </>
        );

        // Sub-tab 2: Content & policies - rich text blocks and cancellation reasons
        const contentFields = (
            <>
                {/* Cancel Description */}
                <Grid2 size={{ xs: 10, sm: 11 }}>
                    <RichTextEditor
                        value={values.cancelDescription}
                        onChange={(val) => setFieldValue('cancelDescription', val)}
                        label={t('shows.columns.cancelDescription')}
                        error={touched.cancelDescription && Boolean(errors.cancelDescription)}
                        helperText={touched.cancelDescription ? errors.cancelDescription : ''}
                    />
                </Grid2>
                <Grid2
                    size={{ xs: 2, sm: 1 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
                >
                    <Tooltip title={t('common.translations')}>
                        <IconButton
                            onClick={() =>
                                showModal(MODAL_TYPE.RICHTEXT_TRANSLATIONS, {
                                    translations: values.cancelDescriptionI18n,
                                    setTranslations: (translations) =>
                                        setFieldValue('cancelDescriptionI18n', translations),
                                    item: 'shows',
                                    column: 'cancelDescription',
                                    venueId: values.venueId,
                                    originalContent: values.cancelDescription,
                                })
                            }
                            disabled={!values.venueId}
                            size="small"
                            edge="end"
                        >
                            <LanguageIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid2>

                {/* Need to know */}
                <Grid2 size={{ xs: 10, sm: 11 }}>
                    <RichTextEditor
                        value={values.needToKnow}
                        onChange={(val) => setFieldValue('needToKnow', val)}
                        label={t('shows.columns.needToKnow')}
                        error={touched.needToKnow && Boolean(errors.needToKnow)}
                        helperText={touched.needToKnow ? errors.needToKnow : ''}
                    />
                </Grid2>
                <Grid2
                    size={{ xs: 2, sm: 1 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
                >
                    <Tooltip title={t('common.translations')}>
                        <IconButton
                            onClick={() =>
                                showModal(MODAL_TYPE.RICHTEXT_TRANSLATIONS, {
                                    translations: values.needToKnowI18n,
                                    setTranslations: (translations) =>
                                        setFieldValue('needToKnowI18n', translations),
                                    item: 'shows',
                                    column: 'needToKnow',
                                    venueId: values.venueId,
                                    originalContent: values.needToKnow,
                                })
                            }
                            disabled={!values.venueId}
                            size="small"
                            edge="end"
                        >
                            <LanguageIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid2>

                {/* Review text */}
                <Grid2 size={{ xs: 10, sm: 11 }}>
                    <RichTextEditor
                        label={t('shows.columns.reviewText')}
                        value={values.reviewText}
                        onChange={(val) => setFieldValue('reviewText', val)}
                        error={touched.reviewText && Boolean(errors.reviewText)}
                        helperText={touched.reviewText ? errors.reviewText : ''}
                    />
                </Grid2>
                <Grid2
                    size={{ xs: 2, sm: 1 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
                >
                    <Tooltip title={t('common.translations')}>
                        <IconButton
                            onClick={() =>
                                showModal(MODAL_TYPE.RICHTEXT_TRANSLATIONS, {
                                    translations: values.reviewTextI18n,
                                    setTranslations: (translations) =>
                                        setFieldValue('reviewTextI18n', translations),
                                    item: 'shows',
                                    column: 'reviewText',
                                    venueId: values.venueId,
                                    originalContent: values.reviewText,
                                })
                            }
                            disabled={!values.venueId}
                            size="small"
                            edge="end"
                        >
                            <LanguageIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid2>

                {/* Review Link (for SMS) */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.reviewLink')}
                        value={values.reviewLink}
                        onChange={(ev) => setFieldValue('reviewLink', ev.target.value)}
                        error={touched.reviewLink && Boolean(errors.reviewLink)}
                        helperText={touched.reviewLink ? errors.reviewLink : ''}
                    />
                </Grid2>

                {/* Cancellation Reasons */}
                <Grid2 size={{ xs: 12 }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1, textAlign: 'left' }}
                    >
                        {t('shows.columns.cancellationReasonsTitle')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {(values.cancellationReasons || []).map((reason, idx) => (
                        <Box
                            key={reason._key ?? idx}
                            sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                        >
                            <TextField
                                label={t('shows.columns.cancellationReasonTitle')}
                                value={reason.title}
                                onChange={(e) => updateReason(idx, { title: e.target.value })}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label={t('shows.columns.cancellationReasonSubtext')}
                                value={reason.subtext}
                                onChange={(e) => updateReason(idx, { subtext: e.target.value })}
                                size="small"
                                sx={{ flex: 2 }}
                            />
                            <Tooltip title={t('shows.columns.cancellationReasonColor')}>
                                <input
                                    type="color"
                                    aria-label={t('shows.columns.cancellationReasonColor')}
                                    value={reason.color || '#002DE5'}
                                    onChange={(e) => updateReason(idx, { color: e.target.value })}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        cursor: 'pointer',
                                        border: 'none',
                                        background: 'none',
                                        padding: 0,
                                        flexShrink: 0,
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={t('shows.columns.cancellationReasonColorReset')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={t('shows.columns.cancellationReasonColorReset')}
                                        onClick={() => updateReason(idx, { color: null })}
                                        disabled={!reason.color}
                                    >
                                        <RestartAltIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <IconButton
                                size="small"
                                aria-label={t('common.delete')}
                                onClick={() =>
                                    setFieldValue(
                                        'cancellationReasons',
                                        values.cancellationReasons.filter((_, i) => i !== idx),
                                    )
                                }
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        size="small"
                        sx={{ display: 'flex', mr: 'auto' }}
                        onClick={() =>
                            setFieldValue('cancellationReasons', [
                                ...(values.cancellationReasons || []),
                                { title: '', subtext: '', _key: crypto.randomUUID() },
                            ])
                        }
                    >
                        {t('shows.addCancellationReason')}
                    </Button>
                </Grid2>
            </>
        );

        // Sub-tab 3: Notifications - sender identity, channels, notification emails
        const notificationFields = (
            <>
                {/* Sender Email Prefix */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.senderEmailPrefix')}
                        value={values.marketing?.senderEmailPrefix || ''}
                        onChange={(ev) => {
                            const raw = ev.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
                            setFieldValue('marketing', {
                                ...values.marketing,
                                senderEmailPrefix: raw || undefined,
                            });
                        }}
                        placeholder="no-reply"
                        helperText={t('shows.columns.senderEmailPrefixHelper')}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        @mg.fire-knife.com
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid2>

                {/* Notification channels */}
                <Grid2 size={{ xs: 12 }}>
                    <FormGroup row sx={{ mt: 0.5, textAlign: 'left' }}>
                        {[
                            {
                                value: 'email',
                                label: t(
                                    'shows.columns.notificationChannels.email',
                                    'Allow email notifications',
                                ),
                            },
                            {
                                value: 'sms',
                                label: t(
                                    'shows.columns.notificationChannels.sms',
                                    'Allow SMS notifications',
                                ),
                            },
                        ].map((channel) => {
                            const channels = Array.isArray(values.notificationChannels)
                                ? values.notificationChannels
                                : [];
                            const checked = channels.includes(channel.value);
                            return (
                                <FormControlLabel
                                    key={channel.value}
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            onChange={(ev) => {
                                                const next = ev.target.checked
                                                    ? [...new Set([...channels, channel.value])]
                                                    : channels.filter((c) => c !== channel.value);
                                                setFieldValue('notificationChannels', next);
                                            }}
                                        />
                                    }
                                    label={channel.label}
                                />
                            );
                        })}
                    </FormGroup>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, mb: 1, textAlign: 'left' }}
                    >
                        {t(
                            'shows.columns.notificationChannelsHelp',
                            'Channels not checked here are blocked for all bookings on this show. Individual bookings can only opt out of channels that are enabled here.',
                        )}
                    </Typography>
                    <FormGroup
                        sx={{
                            textAlign: 'left',
                            '& .MuiFormControlLabel-root': { alignItems: 'flex-start' },
                            '& .MuiCheckbox-root': { pt: 0 },
                        }}
                    >
                        <FormControlLabel
                            label={t('shows.columns.enabledAbandonedCartEmail')}
                            control={
                                <Checkbox
                                    checked={values.enabledAbandonedCartEmail}
                                    onChange={(ev) =>
                                        setFieldValue(
                                            'enabledAbandonedCartEmail',
                                            ev.target.checked,
                                        )
                                    }
                                />
                            }
                        />
                    </FormGroup>
                </Grid2>

                {/* Notification recipient emails */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.waitlistNotificationEmailsLabel')}
                        value={values.waitlistNotificationEmails ?? ''}
                        onChange={(ev) =>
                            setFieldValue('waitlistNotificationEmails', ev.target.value)
                        }
                        error={
                            touched.waitlistNotificationEmails &&
                            Boolean(errors.waitlistNotificationEmails)
                        }
                        helperText={
                            (touched.waitlistNotificationEmails &&
                                errors.waitlistNotificationEmails) ||
                            t('shows.columns.waitlistNotificationEmailsHelp')
                        }
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.checkinNotificationEmailsLabel')}
                        value={values.checkinNotificationEmails ?? ''}
                        onChange={(ev) =>
                            setFieldValue('checkinNotificationEmails', ev.target.value)
                        }
                        error={
                            touched.checkinNotificationEmails &&
                            Boolean(errors.checkinNotificationEmails)
                        }
                        helperText={
                            (touched.checkinNotificationEmails &&
                                errors.checkinNotificationEmails) ||
                            t('shows.columns.checkinNotificationEmailsHelp')
                        }
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('shows.columns.upgradeNotificationEmailsLabel')}
                        value={values.upgradeNotificationEmails ?? ''}
                        onChange={(ev) =>
                            setFieldValue('upgradeNotificationEmails', ev.target.value)
                        }
                        error={
                            touched.upgradeNotificationEmails &&
                            Boolean(errors.upgradeNotificationEmails)
                        }
                        helperText={
                            (touched.upgradeNotificationEmails &&
                                errors.upgradeNotificationEmails) ||
                            t('shows.columns.upgradeNotificationEmailsHelp')
                        }
                    />
                </Grid2>
            </>
        );

        // Sub-tab 4: Pricing - how prices and fees are shown to guests
        const pricingFields = (
            <>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                        <InputLabel id="breaking-out-fees-label">
                            {t('shows.columns.isBreakingOutFees')}
                        </InputLabel>
                        <Select
                            sx={{ textAlign: 'left' }}
                            labelId="breaking-out-fees-label"
                            label={t('shows.columns.isBreakingOutFees')}
                            value={values.isBreakingOutFees}
                            onChange={(ev) => setFieldValue('isBreakingOutFees', ev.target.value)}
                        >
                            <MenuItem value={0}>{t('shows.feeDisplayModes.noBreakdown')}</MenuItem>
                            <MenuItem value={1}>
                                {t('shows.feeDisplayModes.simpleBreakdown')}
                            </MenuItem>
                            <MenuItem value={2}>
                                {t('shows.feeDisplayModes.detailedBreakdown')}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                        <InputLabel id="price-listing-mode-label">
                            {t('shows.columns.priceListingMode')}
                        </InputLabel>
                        <Select
                            sx={{ textAlign: 'left' }}
                            labelId="price-listing-mode-label"
                            label={t('shows.columns.priceListingMode')}
                            value={values.priceListingMode}
                            onChange={(ev) => setFieldValue('priceListingMode', ev.target.value)}
                        >
                            <MenuItem value="gross">{t('shows.priceListingModes.gross')}</MenuItem>
                            <MenuItem value="net">{t('shows.priceListingModes.net')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('shows.columns.bookingFeeRebateShare')}
                        value={
                            values.marketing?.bookingFeeRebateShare != null
                                ? Math.round(values.marketing.bookingFeeRebateShare * 100)
                                : ''
                        }
                        onChange={(ev) => {
                            const raw = ev.target.value;
                            const pct =
                                raw === '' ? undefined : Math.max(0, Math.min(100, Number(raw)));
                            setFieldValue('marketing', {
                                ...values.marketing,
                                bookingFeeRebateShare: pct == null ? undefined : pct / 100,
                            });
                        }}
                        helperText={t('shows.columns.bookingFeeRebateShareHelp')}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                        }}
                        inputProps={{ min: 0, max: 100 }}
                    />
                </Grid2>
            </>
        );

        // Per-sub-tab touched-error counts, so each sub-tab shows a red badge when
        // it holds a validation error the user has interacted with (or after submit).
        const subTabs = [
            {
                label: t('shows.settingsTabs.general'),
                content: generalFields,
                errorCount: [
                    errors.venueId && touched.venueId,
                    errors.location && touched.location,
                    errors.countryCode && touched.countryCode,
                    errors.destinationCode && touched.destinationCode,
                    errors.cancelWindow && touched.cancelWindow,
                    errors.reservationTime && touched.reservationTime,
                    errors.rollbackTimeSize && touched.rollbackTimeSize,
                    errors.bookingWindow && touched.bookingWindow,
                    errors.icalUrl && touched.icalUrl,
                    errors.autoPublishDaysInAdvance && touched.autoPublishDaysInAdvance,
                ].filter(Boolean).length,
            },
            {
                label: t('shows.settingsTabs.bookingRules'),
                content: bookingRulesFields,
                errorCount: 0,
            },
            {
                label: t('shows.settingsTabs.content'),
                content: contentFields,
                errorCount: [
                    errors.cancelDescription && touched.cancelDescription,
                    errors.needToKnow && touched.needToKnow,
                    errors.reviewText && touched.reviewText,
                    errors.reviewLink && touched.reviewLink,
                ].filter(Boolean).length,
            },
            {
                label: t('shows.settingsTabs.notifications'),
                content: notificationFields,
                errorCount: [
                    errors.waitlistNotificationEmails && touched.waitlistNotificationEmails,
                    errors.checkinNotificationEmails && touched.checkinNotificationEmails,
                    errors.upgradeNotificationEmails && touched.upgradeNotificationEmails,
                ].filter(Boolean).length,
            },
            {
                label: t('shows.settingsTabs.pricing'),
                content: pricingFields,
                errorCount: 0,
            },
        ];

        const activeSubTab = subTabs[subTab] ?? subTabs[0];

        return (
            <Grid2 size={{ xs: 12 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 1, md: 3 },
                    }}
                >
                    <Tabs
                        value={subTab}
                        onChange={(_, v) => setSubTab(v)}
                        orientation={stackTabs ? 'horizontal' : 'vertical'}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            flexShrink: 0,
                            minHeight: 40,
                            minWidth: { md: 200 },
                            borderBottom: { xs: 1, md: 0 },
                            borderRight: { md: 1 },
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                minHeight: 40,
                                alignItems: { md: 'flex-start' },
                            },
                        }}
                    >
                        {subTabs.map((tab) => (
                            <Tab
                                key={tab.label}
                                label={
                                    <Badge
                                        badgeContent={tab.errorCount}
                                        color="error"
                                        sx={{ '& .MuiBadge-badge': { right: -14 } }}
                                    >
                                        {tab.label}
                                    </Badge>
                                }
                            />
                        ))}
                    </Tabs>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Grid2 container spacing={2}>
                            {activeSubTab.content}
                        </Grid2>
                    </Box>
                </Box>
            </Grid2>
        );
    },
);

ShowSettingsSection.displayName = 'ShowSettingsSection';

export default ShowSettingsSection;
